import { FEATURE } from "@/constants/entitlements";

const GIGABYTE = 1024 * 1024 * 1024;

/**
 * ฟีเจอร์ที่โควตารวมอยู่ในแพ็ก แต่ "ท่อ" ที่ใช้ส่งไม่ได้รวม คลินิกจ่ายเอง
 *
 * MCP_TOOL_CALL — คลินิกต้องมีบัญชี Claude หรือ ChatGPT ของตัวเองมาเป็น client
 *   ที่เชื่อมเข้า MCP server โควตาที่ขายคือจำนวนครั้งที่เรียกเครื่องมือ ไม่ใช่ค่าบัญชี AI
 * DENTCARE_REMINDER — แจ้งเตือนนัดหมายเป็น push message ซึ่ง LINE คิดเงินต่อข้อความ
 *
 * แชทบอทตอบคนไข้ **ไม่ติดดอกจัน** ทั้งที่เป็น LINE เหมือนกัน เพราะมันตอบด้วย
 * reply token ซึ่งไม่มีค่าส่ง ต่างกันที่ push กับ reply ไม่ใช่ที่ช่องทาง
 *
 * ทำเป็นดอกจันเล็ก ๆ ไม่ใช่คำเตือนเต็มบรรทัด เพราะเป็นเงื่อนไขปลีกย่อย ไม่ใช่สิ่งที่
 * ใช้เลือกแพ็ก แต่ต้องมี ไม่งั้นกลายเป็นบอกราคาไม่ครบ
 */
const FOOTNOTE_KEYS = ["DENTCARE_REMINDER", "MCP_TOOL_CALL"];

/** ตัวเลขแบบไทย (คั่นหลักพัน) — การ์ดราคาและบรรทัดโควตาใช้ร่วมกัน */
export function formatMoney(num) {
  return Number(num || 0).toLocaleString("th-TH");
}

/**
 * แปลง entitlements ของแพ็กเป็นบรรทัดฟีเจอร์ที่การ์ดวางได้ตรง ๆ
 *
 * คืน { key, text, strong, suffix } แทนสตริงเดียวแบบเดิม เพราะดีไซน์ใหม่เน้น
 * "ตัวเลขโควตา" ให้หนา ส่วนฟีเจอร์แบบเปิด/ปิดปล่อยเป็นน้ำหนักปกติ — ผู้ซื้อ
 * เทียบแพ็กกันที่ตัวเลข ไม่ใช่ที่จำนวนบรรทัด ถ้ายังคืนเป็นสตริงเดียว การ์ดจะต้อง
 * ไล่หาตัวเลขเองด้วย regex ซึ่งพังทันทีที่ BE เพิ่มฟีเจอร์ใหม่
 *
 * strong เป็น null = บรรทัดนั้นไม่มีตัวเลขให้เน้น
 *
 * รายการนี้สร้างจาก entitlements ที่ BE ส่งมาทั้งก้อน ไม่ได้ hardcode ว่าแพ็กไหน
 * มีอะไร — เพิ่มสินค้าใหม่ที่ BE แล้วหน้านี้โชว์ให้เอง ยกเว้นคีย์ที่ต้องมีหน่วยหรือ
 * คำอธิบายเฉพาะ ซึ่งแปลไว้ข้างล่าง
 */
const UNLIMITED = "ไม่จำกัด";

export function featureLines(plan) {
  const e = (plan && plan.entitlements) || {};
  const lines = [];

  const storage = e[FEATURE.STORAGE];
  if (storage) {
    lines.push({
      key: FEATURE.STORAGE,
      text: "พื้นที่เก็บไฟล์",
      strong: storage.value === null ? UNLIMITED : `${formatMoney(Math.round(storage.value / GIGABYTE))} GB`,
      suffix: "",
    });
  }

  const discount = e[FEATURE.DISCOUNT_TEMPLATE];
  if (discount && (discount.value === null || discount.value > 0)) {
    lines.push({
      key: FEATURE.DISCOUNT_TEMPLATE,
      text: "เทมเพลตส่วนลด",
      strong: discount.value === null ? UNLIMITED : formatMoney(discount.value),
      suffix: discount.value === null ? "" : "แบบ",
    });
  }

  const group = e[FEATURE.PATIENT_GROUP];
  if (group && (group.value === null || group.value > 0)) {
    lines.push({
      key: FEATURE.PATIENT_GROUP,
      text: "กลุ่มคนไข้",
      strong: group.value === null ? UNLIMITED : formatMoney(group.value),
      suffix: group.value === null ? "" : "กลุ่ม",
    });
  }

  // null = ไม่จำกัด ส่วน 0 = แพ็กนั้นไม่มีฟีเจอร์นี้เลย (แพ็กฟรี) — ต้องแยกสองกรณีนี้
  // เพราะทั้งคู่เป็นค่า falsy เงื่อนไข `if (value)` แบบเดิมจึงกลืนแพ็กไม่จำกัดหายไปทั้งบรรทัด
  const chatbot = e[FEATURE.DENTCARE_CHATBOT];
  if (chatbot && (chatbot.value === null || chatbot.value > 0)) {
    lines.push({
      key: FEATURE.DENTCARE_CHATBOT,
      text: "แชทบอทตอบคนไข้",
      strong: chatbot.value === null ? UNLIMITED : formatMoney(chatbot.value),
      suffix: chatbot.value === null ? "" : "ครั้ง/เดือน",
    });
  }

  const mcp = e[FEATURE.MCP_TOOL_CALL];
  if (mcp && (mcp.value === null || mcp.value > 0)) {
    lines.push({
      key: FEATURE.MCP_TOOL_CALL,
      text: "ผู้ช่วย AI",
      strong: mcp.value === null ? UNLIMITED : formatMoney(mcp.value),
      suffix: mcp.value === null ? "" : "ครั้ง/เดือน",
      footnote: true,
    });
  }

  const ocr = e[FEATURE.RECONCILE_OCR];
  if (ocr && (ocr.value === null || ocr.value > 0)) {
    lines.push({
      key: FEATURE.RECONCILE_OCR,
      text: "อ่านเอกสาร ปกส. ด้วย AI",
      strong: ocr.value === null ? UNLIMITED : formatMoney(ocr.value),
      suffix: ocr.value === null ? "" : "ครั้ง/เดือน",
    });
  }

  if (e[FEATURE.DENTCARE_REMINDER] && e[FEATURE.DENTCARE_REMINDER].value) {
    lines.push({
      key: FEATURE.DENTCARE_REMINDER,
      text: "แจ้งเตือนนัดหมายผ่านแอป DentCare",
      strong: null,
      suffix: "",
      footnote: true,
    });
  }

  if (e[FEATURE.DOCUMENT_GENERATOR] && e[FEATURE.DOCUMENT_GENERATOR].value) {
    lines.push({
      key: FEATURE.DOCUMENT_GENERATOR,
      text: "ใบยินยอม/ใบส่งตัว/ใบรับรองแพทย์ และเซ็นเก็บเข้าแฟ้ม",
      strong: null,
      suffix: "",
    });
  }

  // strong เป็น null เพราะไม่มีตัวเลขให้เน้น การฝืนแยกคำออกมาเป็น strong ทำให้
  // ประโยคภาษาไทยขาดเป็นท่อน ๆ
  if (e[FEATURE.AD_FREE] && e[FEATURE.AD_FREE].value) {
    lines.push({
      key: FEATURE.AD_FREE,
      text: "ไม่มีโฆษณาในแอป",
      strong: null,
      suffix: "",
    });
  }

  return lines;
}

/**
 * แพ็กที่ควรติดป้าย "แนะนำ"
 *
 * BE ไม่ได้ส่งธงนี้มา และห้ามเดาฟิลด์ที่ไม่มีจริง — กติกาจึงตัดสินจากราคาล้วน ๆ:
 * เรียงแพ็กที่มีราคาจากถูกไปแพง แล้วหยิบใบกลางค่อนไปทางถูก
 * (index = floor((n-1)/2)) — 2 ใบได้ใบถูกกว่า, 3 ใบได้ใบกลาง
 *
 * ไม่ติดป้ายเลยเมื่อมีแพ็กเสียเงินใบเดียวหรือไม่มีเลย เพราะ "แนะนำ" ที่ไม่มีอะไร
 * ให้เทียบ ไม่ได้บอกอะไรผู้ใช้ นอกจากกินพื้นที่
 *
 * จัดเรียงบนสำเนา ไม่แตะอาร์เรย์ที่ส่งเข้ามา — ลำดับการ์ดบนหน้าจอเป็นลำดับที่ BE
 * ส่งมา ไม่ใช่ลำดับราคา
 */
export function recommendedPlanCode(plans) {
  const paid = (plans || [])
    .filter((p) => Number(p.priceMonthly) > 0)
    .slice()
    .sort((a, b) => Number(a.priceMonthly) - Number(b.priceMonthly));

  if (paid.length < 2) return null;
  return paid[Math.floor((paid.length - 1) / 2)].code;
}

/**
 * ราคาต่อเดือนโดยเฉลี่ยของแพ็กรายปี — ใช้เป็นบรรทัดรองใต้ราคาเพื่อให้เทียบกับ
 * รายเดือนได้ทันทีโดยไม่ต้องหารเอง คืน null เมื่อไม่มีราคารายปี (เช่นแพ็กฟรี)
 */
export function yearlyMonthlyEquivalent(plan) {
  const yearly = Number(plan && plan.priceYearly);
  if (!yearly) return null;
  return Math.round(yearly / 12);
}

/**
 * ส่วนต่างเป็นบาทต่อปีเมื่อจ่ายรายปีแทนรายเดือน
 *
 * คืน null เมื่อไม่มีส่วนลดจริง — ป้าย "ประหยัด 0 บาท" แย่กว่าไม่มีป้าย และแพ็กฟรี
 * ก็ไม่ควรมีป้ายนี้ ค่าคำนวณสดจากราคาสองตัวที่ BE ส่งมา ไม่ได้ hardcode อัตราส่วน
 * ไว้ที่ไหน (วันนี้รายปี = 10 เท่าของรายเดือน แต่ตารางราคาแก้ได้โดยไม่ต้อง deploy)
 */
export function yearlySaving(plan) {
  const monthly = Number(plan && plan.priceMonthly);
  const yearly = Number(plan && plan.priceYearly);
  if (!monthly || !yearly) return null;
  const saving = monthly * 12 - yearly;
  return saving > 0 ? saving : null;
}

/**
 * ตัวเลขใหญ่บนการ์ด — เป็น "ต่อเดือน" เสมอ ไม่ว่าจะเลือกรอบไหน
 *
 * ราคารายปีเต็มก้อน (19,900) อ่านแล้วแพงกว่ารายเดือน (1,990) ทั้งที่ถูกกว่า การเอา
 * ยอดเต็มขึ้นเป็นตัวหลักจึงทำให้รายปีดูแย่กว่าความจริง หน้านี้ยกค่าเฉลี่ยต่อเดือน
 * ขึ้นมาเป็นตัวหลักแทน แล้วผลักยอดเต็มลงไปเป็นบรรทัดรอง เพื่อให้เทียบกันได้ตรง ๆ
 */
export function monthlyFigure(plan, isYearly) {
  if (!plan) return 0;
  if (!isYearly) return Number(plan.priceMonthly) || 0;
  return yearlyMonthlyEquivalent(plan) || 0;
}

/**
 * ส่วนลดของรายปีเป็นเปอร์เซ็นต์กลม ๆ สำหรับป้าย "ลด 17%" บนปุ่มสลับรอบ
 *
 * คำนวณสดจากราคาสองตัวที่ BE ส่งมา ไม่ hardcode — วันนี้รายปี = 10 เท่าของรายเดือน
 * ได้ 16.7% ปัดเป็น 17 ถ้าวันหนึ่งแต่ละแพ็กลดไม่เท่ากัน ใช้ตัวที่น้อยที่สุด เพราะป้าย
 * ใบเดียวแปะอยู่บนปุ่มที่คุมทุกการ์ด ตัวเลขต้องจริงกับทุกแพ็ก ไม่ใช่แค่แพ็กที่ลดเยอะสุด
 *
 * คืน null เมื่อไม่มีแพ็กไหนลดเลย — ป้าย "ลด 0%" แย่กว่าไม่มีป้าย
 */
export function yearlyDiscountPercent(plans) {
  const percents = (plans || [])
    .map((p) => {
      const saving = yearlySaving(p);
      return saving ? Math.round((saving / (Number(p.priceMonthly) * 12)) * 100) : null;
    })
    .filter((n) => n > 0);
  return percents.length ? Math.min(...percents) : null;
}

/**
 * ส่วนลดรายปีคิดเป็นกี่เดือนของราคารายเดือน — "ใช้ฟรี 2 เดือน"
 *
 * คืนเฉพาะเมื่อหารลงตัวเป็นเดือนเต็ม ถ้าเป็น 1.7 เดือนให้คืน null แล้วหน้าจอพูดเป็นบาทแทน
 * เพราะ "ใช้ฟรี 1.7 เดือน" อ่านแล้วไม่ใช่ข้อเสนอ มันคือการบ้าน
 */
export function freeMonths(plan) {
  const saving = yearlySaving(plan);
  const monthly = Number(plan && plan.priceMonthly);
  if (!saving || !monthly) return null;
  const months = saving / monthly;
  const whole = Math.round(months);
  return whole >= 1 && Math.abs(months - whole) < 0.001 ? whole : null;
}

/** จำนวนเดือนฟรีที่ทุกแพ็กเสียเงินได้เท่ากัน — ไม่เท่ากันคืน null เพราะข้อความข้างปุ่มสลับรอบพูดแทนทุกแพ็ก */
export function commonFreeMonths(plans) {
  const paid = (plans || []).filter((p) => yearlySaving(p));
  if (!paid.length) return null;
  const months = freeMonths(paid[0]);
  return months && paid.every((p) => freeMonths(p) === months) ? months : null;
}

const FLAG_KEYS = [FEATURE.DENTCARE_REMINDER, FEATURE.DOCUMENT_GENERATOR, FEATURE.AD_FREE];

const isFlagKey = (key) => FLAG_KEYS.indexOf(key) !== -1;

/** โควตาเป็นตัวเลขที่เทียบกันได้: ไม่มีคีย์ = 0, null (ไม่จำกัด) = Infinity */
function quotaOf(entitlements, key) {
  const e = (entitlements || {})[key];
  if (!e) return 0;
  if (e.value === null) return Infinity;
  return Number(e.value) || 0;
}

function flagOf(entitlements, key) {
  const e = (entitlements || {})[key];
  return !!(e && e.value);
}

/**
 * ป้ายตัวคูณ "×4" ระหว่างโควตาสองตัว — คืน null เมื่อไม่มีอะไรน่าพูด
 *
 * ต่ำกว่าสองเท่าไม่ติดป้าย เพราะ "×1.5" อ่านแล้วเล็กกว่าที่เป็นจริง และตัวเลขข้าง ๆ
 * ก็บอกอยู่แล้ว ส่วนไม่จำกัดไม่ใช่ตัวคูณ ผู้เรียกตัดสินเองว่าจะพูดยังไง
 */
function multiplierChip(current, previous) {
  if (!(previous > 0) || !Number.isFinite(previous) || !Number.isFinite(current)) return null;
  const ratio = current / previous;
  return ratio >= 2 ? `×${formatMoney(Math.round(ratio))}` : null;
}

/** แพ็กนี้ได้ทุกอย่างที่แพ็ก base ได้หรือไม่ — ใช้ตัดสินว่าเขียน "ทุกอย่างในแพ็ก X" ได้จริงไหม */
function covers(plan, base) {
  const mine = plan.entitlements || {};
  return Object.keys(base.entitlements || {}).every((key) =>
    isFlagKey(key)
      ? !flagOf(base.entitlements, key) || flagOf(mine, key)
      : quotaOf(mine, key) >= quotaOf(base.entitlements, key)
  );
}

/**
 * แพ็กที่การ์ดนี้เขียนว่า "ทุกอย่างในแพ็ก X และ" — แพ็กที่ถูกกว่าถัดลงไปหนึ่งขั้น
 *
 * ต้องครอบคลุมจริงเท่านั้น ถ้าแพ็กถูกกว่ามีอะไรสักอย่างที่แพ็กนี้ไม่มี (BE จัดแคตตาล็อก
 * แบบไม่ไล่ขั้น) ประโยคนั้นกลายเป็นคำโกหก จึงคืน null แล้วการ์ดกลับไปกางรายการเต็ม
 */
function basePlanOf(plan, plans) {
  const price = Number(plan && plan.priceMonthly) || 0;
  const cheaper = (plans || [])
    .filter((p) => p !== plan && p.code !== plan.code && (Number(p.priceMonthly) || 0) < price)
    .sort((a, b) => (Number(b.priceMonthly) || 0) - (Number(a.priceMonthly) || 0));
  const base = cheaper[0];
  return base && covers(plan, base) ? base : null;
}

/**
 * บรรทัดบนการ์ด — ตอบ "จ่ายแพงกว่าได้อะไร" ในการ์ดเอง
 *
 * แพ็กที่มีแพ็กถูกกว่าให้อ้าง โชว์เฉพาะสิ่งที่ได้เพิ่มจากแพ็กนั้น พร้อมป้าย
 * ×N (โควตาขึ้นเป็นเท่า) / ∞ (กลายเป็นไม่จำกัด) / ใหม่ (แพ็กก่อนไม่มีเลย)
 * ส่วนธงเปิด/ปิดที่เพิ่งได้ไม่มีป้าย — ตัวบรรทัดบอกอยู่แล้วว่าได้อะไร
 *
 * คืน { base, lines } — base เป็น null เมื่อไม่มีแพ็กให้อ้าง (แพ็กถูกสุด หรือแพ็กก่อนหน้า
 * ไม่ถูกครอบคลุม) ซึ่งการ์ดต้องกางรายการเต็มแทน
 */
export function planFeatures(plan, plans) {
  const all = featureLines(plan);
  const full = { base: null, lines: all.map((line) => ({ ...line, chip: null })) };
  const base = basePlanOf(plan, plans);
  if (!base) return full;

  const mine = plan.entitlements || {};
  const theirs = base.entitlements || {};
  const lines = all
    .map((line) => {
      if (isFlagKey(line.key)) {
        return flagOf(mine, line.key) && !flagOf(theirs, line.key) ? { ...line, chip: null } : null;
      }
      const now = quotaOf(mine, line.key);
      const before = quotaOf(theirs, line.key);
      if (!(now > before)) return null;
      if (now === Infinity) return { ...line, chip: "∞" };
      if (before === 0) return { ...line, chip: "ใหม่" };
      return { ...line, chip: multiplierChip(now, before) };
    })
    .filter(Boolean);

  // แพ็กที่แพงกว่าแต่ไม่ได้อะไรเพิ่มเลยไม่ควรมีอยู่ แต่ถ้ามี อย่าวาดการ์ดว่าง ๆ
  return lines.length ? { base, lines } : full;
}

/**
 * ตารางเปรียบเทียบใต้การ์ด
 *
 * การ์ดตอบคำถาม "แพ็กนี้ได้อะไร" ตารางตอบ "ต่างกันตรงไหน" ซึ่งการ์ดสี่ใบเรียงกัน
 * ตอบไม่ได้ — ตาเราไม่ได้เทียบเลขข้ามคอลัมน์จากบุลเล็ตสี่ชุดที่ยาวไม่เท่ากัน
 *
 * แถวมาจาก union ของคีย์ที่แพ็กไหนก็ตามมี ไม่ได้ hardcode — BE เพิ่มฟีเจอร์ใหม่แล้ว
 * แถวใหม่ขึ้นเอง ส่วนคีย์ที่ไม่มีใครมีเลยก็ไม่กินที่
 *
 * แถวจัดเป็นกลุ่มตามงานของคลินิก ไม่ใช่ตามชนิดข้อมูล — คนอ่านถามว่า "เรื่องดูแลคนไข้
 * ต่างกันยังไง" ไม่ได้ถามว่า "โควตาแบบนับครั้งต่างกันยังไง"
 *
 * varies: แถวที่ทุกแพ็กได้เท่ากันไม่ใช่เหตุผลในการเลือกแพ็ก หน้าจอจึงหรี่มันลง
 */
const ROW_GROUPS = [
  {
    title: "พื้นที่และข้อมูลคนไข้",
    keys: [FEATURE.STORAGE, FEATURE.DISCOUNT_TEMPLATE, FEATURE.PATIENT_GROUP],
  },
  { title: "ดูแลคนไข้อัตโนมัติ", keys: [FEATURE.DENTCARE_CHATBOT, FEATURE.DENTCARE_REMINDER] },
  {
    title: "เอกสารและผู้ช่วย AI",
    keys: [FEATURE.DOCUMENT_GENERATOR, FEATURE.MCP_TOOL_CALL, FEATURE.RECONCILE_OCR],
  },
  { title: "การใช้งาน", keys: [FEATURE.AD_FREE] },
];

const ROW_ORDER = ROW_GROUPS.reduce((keys, group) => keys.concat(group.keys), []);

const ROW_LABEL = {
  [FEATURE.STORAGE]: "พื้นที่เก็บไฟล์",
  [FEATURE.DISCOUNT_TEMPLATE]: "เทมเพลตส่วนลด",
  [FEATURE.PATIENT_GROUP]: "กลุ่มคนไข้",
  [FEATURE.DENTCARE_CHATBOT]: "แชทบอทตอบคนไข้ (ครั้ง/เดือน)",
  [FEATURE.MCP_TOOL_CALL]: "ผู้ช่วย AI (ครั้ง/เดือน)",
  [FEATURE.RECONCILE_OCR]: "อ่านเอกสาร ปกส. ด้วย AI (ครั้ง/เดือน)",
  [FEATURE.DENTCARE_REMINDER]: "แจ้งเตือนนัดหมายผ่านแอป DentCare",
  [FEATURE.DOCUMENT_GENERATOR]: "ใบยินยอม ใบส่งตัว ใบรับรองแพทย์ และเซ็นเก็บเข้าแฟ้ม",
  [FEATURE.AD_FREE]: "ไม่มีโฆษณาในแอป",
};

/**
 * ค่าในช่องหนึ่งของตาราง
 *
 * ตัดสินจากคีย์ ไม่ใช่จาก entitlement.kind ที่ BE ส่งมา — ฝั่งนี้รู้อยู่แล้วว่าคีย์ไหน
 * เป็นตัวเลขคีย์ไหนเป็นเปิด/ปิด และไม่ต้องพังถ้าวันหนึ่ง kind หายไปจาก payload
 *
 * kind: value = มีตัวเลข, unlimited = ไม่จำกัด, yes = เปิดใช้ได้, no = ไม่ได้
 * ไม่จำกัดแยกจาก yes เพราะหน้าจอวาดต่างกัน (ป้าย ∞ กับเครื่องหมายถูก)
 */
function cellFor(key, entitlements) {
  const e = (entitlements || {})[key];
  if (!e) return { kind: "no", text: "—" };

  if (isFlagKey(key)) {
    return e.value ? { kind: "yes", text: "มี" } : { kind: "no", text: "—" };
  }
  if (e.value === null) return { kind: "unlimited", text: UNLIMITED };
  if (!e.value) return { kind: "no", text: "—" };
  if (key === FEATURE.STORAGE) {
    return { kind: "value", text: `${formatMoney(Math.round(e.value / GIGABYTE))} GB` };
  }
  return { kind: "value", text: formatMoney(e.value) };
}

export function comparisonRows(plans) {
  const list = plans || [];
  if (!list.length) return [];

  return ROW_ORDER.filter((key) =>
    list.some((p) => p.entitlements && p.entitlements[key])
  ).map((key) => {
    // step = ป้าย ×N เทียบกับคอลัมน์ซ้ายมือ — ตอบ "ขึ้นแพ็กนี้แล้วได้มากขึ้นเท่าไร" ในแถวเดียว
    // ช่องที่คอลัมน์ก่อนหน้าไม่มีของเลยไม่มีป้าย ตัวเลขที่เพิ่งโผล่บอกเรื่องของมันเอง
    const cells = list.map((p, i) => {
      const cell = cellFor(key, p.entitlements);
      cell.step =
        cell.kind === "value" && i > 0
          ? multiplierChip(quotaOf(p.entitlements, key), quotaOf(list[i - 1].entitlements, key))
          : null;
      return cell;
    });

    return {
      key,
      label: ROW_LABEL[key],
      footnote: FOOTNOTE_KEYS.indexOf(key) !== -1,
      cells,
      varies: cells.some((c) => c.text !== cells[0].text),
    };
  });
}

/** แถวของตารางจัดเป็นกลุ่ม — กลุ่มที่ไม่เหลือแถวไม่กินที่ */
export function comparisonGroups(plans) {
  const rows = comparisonRows(plans);
  return ROW_GROUPS.map((group) => ({
    title: group.title,
    rows: rows.filter((row) => group.keys.indexOf(row.key) !== -1),
  })).filter((group) => group.rows.length);
}
